import { CircleHelp, Loader2, ShieldAlert } from 'lucide-react';
import {
  type ReactNode,
  useEffect,
  useId,
  useLayoutEffect,
  useRef,
  useState,
  useSyncExternalStore,
} from 'react';
import { ApiError, reportClientError } from '../../lib/errors';
import { ErrorCodes, errorCode, errorSentence } from '../ErrorToast';
import { Badge, Button, textareaCls } from '../ui';
import { CardHead } from './CardHead';
import { CARD_TEXTS } from './texts-cards';
import type { RequestCardProps, RequestDecision, RequestHistoryProps, RequestRow } from './types';

/*
 * Запрос агента (006, T089; US4-2 – US4-4; FR-027–FR-030, FR-040; contracts/ui.md §2; мокап Main.dc.html,
 * PhoneApproval.dc.html).
 *
 * `RequestCard` — карточка вместо поля сообщения, пока запрос ждёт ответа:
 * - заголовок и бейдж «ждёт ответа»; что сделает агент; «Почему спрашиваем», «После «Разрешить»», «После «Запретить»»
 *   (тексты сервера, `cards.ts`); срок «Если ответа не будет 25 минут, запрос отменится»;
 * - кнопки — по `decisions` сервера в постоянном порядке: «Запретить» → «Разрешить в этом разговоре» → главная
 *   «Разрешить один раз» последней справа; у вопроса — варианты (радио или флажки), «Не отвечать» и «Отправить ответ»;
 * - пока ответ уходит, кнопки недоступны, у нажатой — значок отправки на месте подписи (ширина та же); повторного
 *   ответа нет. Ошибка — на карточке с кодом ошибки; «Запрос уже не ждёт ответа» (`INTERACTION_CLOSED`) — кнопки
 *   остаются недоступны: строку закроет лента;
 * - запрос закрыт (ответ здесь или в другой вкладке, остановка, срок) — карточки нет: ответ приходит новой строкой
 *   ленты. Фокус после ответа — в поле сообщения: у поля атрибут `data-assistant-composer` (`COMPOSER_ATTR`); если
 *   ждёт ещё один запрос — на его карточку (FR-029, FR-040);
 * - карточка появилась, когда фокуса на странице нет (поле сообщения ушло), — фокус на саму карточку, не на кнопку:
 *   Enter, нажатый в этот миг, ничего не разрешит;
 * - раскладка — по ширине самой карточки (`@container`): в узкой панели пояснения в один столбец, кнопки во всю
 *   ширину, главная сверху; на телефоне кнопки 40 px;
 * - при «уменьшить движение» значок отправки не вращается.
 *
 * `RequestHistory` — строка истории в ленте: бейдж состояния, о чём спрашивал агент и кто ответил.
 */

/** Атрибут поля сообщения: сюда возвращается фокус после ответа на запрос (договор с задачей ленты). */
export const COMPOSER_ATTR = 'data-assistant-composer';
/** Атрибут карточки запроса: следующий ждущий запрос получает фокус, если поля сообщения ещё нет. */
const CARD_ATTR = 'data-request-card';

const { label, text, history, historyBy } = CARD_TEXTS;
const SCREEN = 'Assistant';

/** После ответа — фокус в поле сообщения; поля нет (ждёт ещё запрос) — на карточку следующего запроса. */
export function focusComposer(): void {
  setTimeout(() => {
    const next =
      document.querySelector<HTMLElement>(`[${COMPOSER_ATTR}]`) ??
      document.querySelector<HTMLElement>(`[${CARD_ATTR}]`);
    next?.focus();
  }, 0);
}

const REDUCED = '(prefers-reduced-motion: reduce)';
function subscribeReduced(onChange: () => void): () => void {
  const mq = typeof window.matchMedia === 'function' ? window.matchMedia(REDUCED) : null;
  mq?.addEventListener?.('change', onChange);
  return () => mq?.removeEventListener?.('change', onChange);
}
const readReduced = () => typeof window.matchMedia === 'function' && window.matchMedia(REDUCED).matches;

/** «Уменьшить движение» в системе: значки не вращаются (FR-040). */
function useReducedMotion(): boolean {
  return useSyncExternalStore(subscribeReduced, readReduced, () => false);
}

/** Сбой связи, сервера или ответ не по договору — в серверный журнал (принцип II); закрытый запрос — нет. */
const shouldReport = (e: unknown) =>
  !(e instanceof ApiError) || e.code === 'NETWORK' || e.code === 'INTERNAL' || e.status >= 500;
const isClosed = (e: unknown) => e instanceof ApiError && e.code === 'INTERACTION_CLOSED';

const ORDER: readonly Exclude<RequestDecision, 'answer'>[] = ['deny', 'allow_thread', 'allow_once'];
const DECISION_LABEL: Record<RequestDecision, string> = {
  deny: label.deny,
  allow_thread: label.allowThread,
  allow_once: label.allowOnce,
  answer: label.answer,
};

export function RequestCard(props: RequestCardProps) {
  // Свой экземпляр на каждый запрос: ответ, ошибка и выбор одного запроса не переходят на следующий (FR-029).
  return <Card key={props.row.key} {...props} />;
}

function Card({ row, onResolve }: RequestCardProps) {
  const titleId = useId();
  const root = useRef<HTMLElement>(null);
  const reduced = useReducedMotion();
  const [busy, setBusy] = useState<RequestDecision | null>(null);
  const [sent, setSent] = useState(false);
  const [failure, setFailure] = useState<unknown>(null);
  const [picked, setPicked] = useState<readonly string[]>([]);
  const [free, setFree] = useState('');
  /** Ответ дан здесь или фокус был в карточке: когда карточка закроется, фокус — в поле сообщения. */
  const answered = useRef(false);
  const focusInside = useRef(false);
  const waiting = row.state === 'waiting';

  // Карточка появилась, а фокуса нет (поле сообщения ушло со страницы) — фокус на карточку, не на кнопку ответа.
  useEffect(() => {
    const a = document.activeElement;
    if (!a || a === document.body) root.current?.focus({ preventScroll: true });
  }, []);

  // Запрос закрылся (ответ здесь или в другой вкладке) или карточку убрали — фокус в поле сообщения.
  const handOff = useRef(false);
  useLayoutEffect(() => {
    if (!waiting && !handOff.current && (answered.current || focusInside.current)) {
      handOff.current = true;
      focusComposer();
    }
  }, [waiting]);
  useLayoutEffect(
    () => () => {
      if (!handOff.current && (answered.current || focusInside.current)) {
        handOff.current = true;
        focusComposer();
      }
    },
    [],
  );

  if (!waiting) return null;

  const question = row.reason === 'question' ? row.question : null;
  const options = question?.options ?? [];
  const answers = options.length > 0 ? picked : free.trim() ? [free.trim()] : [];
  const locked = busy !== null || sent || isClosed(failure);

  const resolve = async (decision: RequestDecision) => {
    if (locked) return;
    setBusy(decision);
    setFailure(null);
    answered.current = true;
    try {
      await onResolve(decision, decision === 'answer' ? [...answers] : undefined);
      setSent(true);
    } catch (e) {
      answered.current = false;
      setFailure(e);
      if (shouldReport(e)) reportClientError(SCREEN, 'resolve-request', e);
    } finally {
      setBusy(null);
    }
  };

  const decisions: RequestDecision[] = question
    ? [...(row.decisions.includes('deny') ? (['deny'] as const) : []), 'answer']
    : ORDER.filter((d) => row.decisions.includes(d));
  const main: RequestDecision = question ? 'answer' : 'allow_once';
  const title = question ? label.questionTitle : (row.card?.title ?? label.approvalTitle);

  return (
    <section
      ref={root}
      tabIndex={-1}
      aria-labelledby={titleId}
      {...{ [CARD_ATTR]: '' }}
      onFocus={() => {
        focusInside.current = true;
      }}
      onBlur={(e) => {
        if (!e.currentTarget.contains(e.relatedTarget as Node | null)) focusInside.current = false;
      }}
      className="@container rounded-card border border-line bg-surface p-4 shadow-card outline-none focus-visible:ring-2 focus-visible:ring-accent focus-visible:ring-offset-2"
    >
      <div className="flex flex-col gap-2.5">
        <CardHead
          icon={question ? CircleHelp : ShieldAlert}
          id={titleId}
          title={title}
          badge={
            <Badge tone="warn" dot>
              {label.waiting}
            </Badge>
          }
        />

        {question ? (
          <Question
            text={question.text}
            options={options}
            multi={question.multiSelect === true}
            picked={picked}
            onPick={setPicked}
            free={free}
            onFree={setFree}
            disabled={locked}
          />
        ) : (
          row.card && (
            <>
              <p className="text-[14px] break-words text-ink">{row.card.what}</p>
              <dl className="grid grid-cols-[max-content_minmax(0,1fr)] gap-x-3 gap-y-1 text-[13px] @max-[480px]:grid-cols-1 @max-[480px]:gap-y-0">
                <Part term={label.why}>{row.card.why}</Part>
                <Part term={label.afterAllow}>{row.card.yes}</Part>
                <Part term={label.afterDeny}>{row.card.no}</Part>
              </dl>
            </>
          )
        )}

        <p className="text-[12px] text-ink-2">{text.deadline}</p>
        {sent && (
          <p role="status" className="text-[12px] text-ink-2">
            {text.sent}
          </p>
        )}
        {failure !== null && <Failure error={failure} />}

        <div
          data-actions
          className="flex flex-wrap justify-end gap-2 @max-[480px]:flex-col-reverse @max-[480px]:items-stretch"
        >
          {decisions.map((d) => (
            <Button
              key={d}
              variant={d === main ? 'primary' : 'secondary'}
              disabled={locked || (d === 'answer' && answers.length === 0)}
              aria-busy={busy === d ? true : undefined}
              onClick={() => void resolve(d)}
            >
              <Busy on={busy === d} reduced={reduced}>
                {question && d === 'deny' ? label.skip : DECISION_LABEL[d]}
              </Busy>
            </Button>
          ))}
        </div>
      </div>
    </section>
  );
}

function Part({ term, children }: { term: string; children: ReactNode }) {
  return (
    <>
      <dt className="text-ink-2 @max-[480px]:mt-1.5">{term}</dt>
      <dd className="break-words text-ink">{children}</dd>
    </>
  );
}

/** Подпись кнопки; пока ответ уходит — значок на её месте, ширина та же (дизайн-система, Button). */
function Busy({ on, reduced, children }: { on: boolean; reduced: boolean; children: ReactNode }) {
  return (
    <span className="relative inline-flex items-center justify-center">
      <span className={on ? 'opacity-0' : undefined}>{children}</span>
      {on && (
        <Loader2
          size={16}
          strokeWidth={1.75}
          aria-hidden
          className={`absolute ${reduced ? '' : 'animate-spin motion-reduce:animate-none'}`}
        />
      )}
    </span>
  );
}

function Failure({ error }: { error: unknown }) {
  const requestId = error instanceof ApiError ? error.requestId : null;
  const code = errorCode(error);
  return (
    <div role="alert" className="rounded-control bg-bad-soft px-3 py-2 text-[13px] text-bad">
      {!isClosed(error) && <p className="font-semibold">{text.answerFailed}</p>}
      <p>{errorSentence(error)}</p>
      {(requestId || code) && (
        <p className="mt-0.5 text-[12px]">
          {label.errorCode} <ErrorCodes requestId={requestId} code={code} />
        </p>
      )}
    </div>
  );
}

type Option = NonNullable<RequestRow['question']>['options'][number];

/** Вопрос агента (FR-030): текст, варианты — радио или флажки; без вариантов — поле ответа. */
function Question({
  text: q,
  options,
  multi,
  picked,
  onPick,
  free,
  onFree,
  disabled,
}: {
  text: string;
  options: readonly Option[];
  multi: boolean;
  picked: readonly string[];
  onPick: (v: readonly string[]) => void;
  free: string;
  onFree: (v: string) => void;
  disabled: boolean;
}) {
  const name = useId();
  const fieldId = useId();
  if (options.length === 0)
    return (
      <>
        <p className="text-[14px] break-words text-ink">{q}</p>
        <div>
          <label htmlFor={fieldId} className="mb-1.5 block text-[13px] font-medium text-ink">
            {label.freeAnswer}
          </label>
          <textarea
            id={fieldId}
            rows={2}
            value={free}
            disabled={disabled}
            placeholder={text.freePlaceholder}
            maxLength={500}
            onChange={(e) => onFree(e.target.value)}
            className={`${textareaCls} resize-none`}
          />
        </div>
      </>
    );
  const toggle = (v: string) =>
    onPick(multi ? (picked.includes(v) ? picked.filter((x) => x !== v) : [...picked, v]) : [v]);
  return (
    <fieldset className="min-w-0">
      <legend className="mb-2 text-[14px] break-words text-ink">{q}</legend>
      <div className="flex flex-col gap-1" aria-label={label.options} role={multi ? 'group' : 'radiogroup'}>
        {options.map((o) => (
          <label
            key={o.label}
            className="flex cursor-pointer items-start gap-2.5 rounded-control px-2 py-1.5 text-[14px] transition-colors hover:bg-subtle max-sm:min-h-10"
          >
            <input
              type={multi ? 'checkbox' : 'radio'}
              name={name}
              checked={picked.includes(o.label)}
              disabled={disabled}
              onChange={() => toggle(o.label)}
              className="mt-[3px] h-4 w-4 shrink-0 accent-accent"
            />
            <span className="min-w-0">
              <span className="block break-words text-ink">{o.label}</span>
              {o.description && (
                <span className="block text-[13px] break-words text-ink-2">{o.description}</span>
              )}
            </span>
          </label>
        ))}
      </div>
    </fieldset>
  );
}

/** Состояние строки → тон бейджа: смысл цвета продублирован подписью. */
const HISTORY_TONE = {
  waiting: 'warn',
  allowed_once: 'ok',
  allowed_thread: 'ok',
  denied: 'neutral',
  cancelled: 'neutral',
  expired: 'neutral',
  answered: 'ok',
} as const satisfies Record<RequestRow['state'], 'warn' | 'ok' | 'neutral'>;

/** Кто закрыл запрос — предложение после бейджа. */
function whoText(r: RequestRow): string | null {
  switch (r.state) {
    case 'waiting':
      return null;
    case 'allowed_once':
      return historyBy.allowed;
    case 'allowed_thread':
      return r.by === 'thread-allow' ? historyBy.threadAllow : historyBy.allowed;
    case 'denied':
      return historyBy.denied;
    case 'answered':
      return historyBy.answered;
    case 'expired':
      return historyBy.expired;
    case 'cancelled':
      return historyBy.cancelled;
  }
}

/** Строка истории запроса в ленте: бейдж состояния, о чём спрашивал агент, кто ответил. */
export function RequestHistory({ row }: RequestHistoryProps) {
  const subject = row.reason === 'question' ? row.question?.text : row.card?.what;
  const who = whoText(row);
  return (
    <p data-request-history className="flex flex-wrap items-center gap-x-2 gap-y-1 text-[13px] text-ink-2">
      <Badge tone={HISTORY_TONE[row.state]} dot>
        {history[row.state]}
      </Badge>
      {subject && <span className="min-w-0 break-words">{subject}</span>}
      {who && <span>{who}</span>}
    </p>
  );
}
